import { useEffect } from 'react';

// Utility to get cookie by name
function getCookie(name: string): string | null {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

// Utility to get URL parameter by name
function getUrlParameter(name: string): string | null {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(window.location.search);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

// Utility to get value from cookie or URL param
function getValueFromCookieOrURL(name: string): string | null {
  return getCookie(name) || getUrlParameter(name) || null;
}

// Utility to check if a link is external
function isExternalLink(url: string): boolean {
  try {
    const link = new URL(url, window.location.href);
    return link.origin !== window.location.origin;
  } catch {
    return false;
  }
}

// Utility to check if a link is a zenoti.com link
function isZenotiLink(url: string): boolean {
  try {
    return url.includes('zenoti.com');
  } catch {
    return false;
  }
}

const ExternalLinkHandler = () => {
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      // Only left click, no modifier keys
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      let target = event.target as HTMLElement | null;
      while (target && !(target instanceof HTMLAnchorElement) && !(target instanceof HTMLButtonElement)) {
        target = target.parentElement;
      }
      if (!target) return;
      let link: string | null = null;
      if (target instanceof HTMLAnchorElement) link = target.href;
      if (target instanceof HTMLButtonElement) link = target.getAttribute('data-link');
      if (!link || link.startsWith('tel:')) return;
      if (!isExternalLink(link)) return;
      if (!isZenotiLink(link)) return; // Only affect zenoti.com links
      
      event.preventDefault();
      // Stop propagation to prevent duplicate event tracking from EventTracker
      event.stopPropagation();
      
      // Track Zenoti link click with additional data
      if (typeof window.trackEvent === 'function') {
        const section = target.getAttribute('data-section') || 'unknown';
        const eventName = target.getAttribute('data-event') || `${section}_zenoti_click`;
        
        window.trackEvent(eventName, {
          href: link,
          element_text: target.textContent?.trim() || '',
          element_type: target.tagName.toLowerCase()
        });
      }
      
      // Get UTM values
      const utm_medium = getValueFromCookieOrURL('utm_medium') || getCookie('_sdid') || '';
      const utm_source = getValueFromCookieOrURL('utm_source') || getCookie('leadPlatform') || '';
      const url = new URL(link);
      url.searchParams.set('utm_medium', utm_medium);
      url.searchParams.set('utm_source', utm_source);
      window.location.href = url.toString();
    }
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);
  return null;
};

export default ExternalLinkHandler; 